import { type MouseEvent, type ReactElement } from 'react';
import { getActionAriaLabel, getActionLabel, getQuickSortActionCopy } from '@/shared/copy/actions';
import { componentSizes } from '@/theme/componentSizes';
import { r } from '@/theme/tokens';
import { Box, Cancel, IconButton, Tooltip } from '@/ui/primitives';
import {
  ChecklistRtlOutlinedIcon,
  FlakyIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  ReplayOutlinedIcon,
  SortOutlinedIcon,
  TimerOutlinedIcon,
  ViewColumnOutlinedIcon,
} from '@/ui/primitives/icons';
import { tableQuickSortBadgeSurfaceSx } from './tablePanelSurfaceSx';
import { buildToolbarIconCircleSx } from './tableToolbar.helpers';
import type * as TableToolbarHelpers from './tableToolbar.helpers';
import type { TableQuickSortMetric, TableQuickSortOption } from './tableToolbar.types';
import type { ToolbarHoverTarget } from './useTableToolbarState';

const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: 'subtitle1.fontSize',
      lineHeight: 1.4,
    },
  },
} as const;

type TableToolbarActionsProps<TSortKey extends string> = {
  hasActiveSort: boolean;
  hasActiveTableToolbarState: boolean;
  hasNonDefaultColumns: boolean;
  hoverTarget: ToolbarHoverTarget;
  onColumnsOpen: (event: MouseEvent<HTMLElement>) => void;
  onQuickSort: (nextSortKey: TSortKey, firstDirection: 'asc' | 'desc') => void;
  onReset: () => void;
  onSetHoverTarget: (target: ToolbarHoverTarget) => void;
  onSortOpen: (event: MouseEvent<HTMLElement>) => void;
  quickSortOptions: Array<TableQuickSortOption<TSortKey>>;
  sortDirection: 'asc' | 'desc';
  sortKey: TSortKey;
  toolbarControlSize: number;
  toolbarIconButtonSx: ReturnType<typeof TableToolbarHelpers.buildToolbarIconButtonSx>;
  toolbarInactiveIconColor: string;
};

const renderQuickSortIcon = (metric: TableQuickSortMetric, color: string): ReactElement => {
  const iconSx = { color, width: '21px !important', height: '21px !important' } as const;
  const compactIconSx = { color, width: '19px !important', height: '19px !important' } as const;
  if (metric === 'passRate') {
    return <ChecklistRtlOutlinedIcon sx={[iconSx, { transform: 'translateY(1px)' }]} />;
  }
  if (metric === 'frequency') {
    return <ReplayOutlinedIcon sx={compactIconSx} />;
  }
  if (metric === 'flakeScore') {
    return <FlakyIcon sx={[compactIconSx, { transform: 'translateY(1px)' }]} />;
  }
  return <TimerOutlinedIcon sx={[compactIconSx, { transform: 'translateY(1px)' }]} />;
};

const renderQuickSortDirectionBadge = (sortDirection: 'asc' | 'desc'): ReactElement => {
  const DirectionIcon = sortDirection === 'asc' ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  const badgeSize = componentSizes.dataGrid.quickSortDirectionBadgeSize;
  return (
    <Box
      aria-hidden
      sx={[
        tableQuickSortBadgeSurfaceSx,
        {
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: r('pill'),
          boxSizing: 'border-box',
          color: 'primary.main',
          display: 'flex',
          height: badgeSize,
          justifyContent: 'center',
          position: 'absolute',
          right: -4,
          top: -2,
          width: badgeSize,
        },
      ]}
    >
      <DirectionIcon
        sx={{ height: `${badgeSize}px !important`, width: `${badgeSize}px !important` }}
      />
    </Box>
  );
};

export const TableToolbarActions = <TSortKey extends string>({
  hasActiveSort,
  hasActiveTableToolbarState,
  hasNonDefaultColumns,
  hoverTarget,
  onColumnsOpen,
  onQuickSort,
  onReset,
  onSetHoverTarget,
  onSortOpen,
  quickSortOptions,
  sortDirection,
  sortKey,
  toolbarControlSize,
  toolbarIconButtonSx,
  toolbarInactiveIconColor,
}: TableToolbarActionsProps<TSortKey>): ReactElement => {
  const toolbarIconCircleSx = buildToolbarIconCircleSx;
  const toolbarIconTooltipDelayMs = 250;

  return (
    <>
      <Tooltip
        title={getActionLabel('columns')}
        arrow
        enterDelay={toolbarIconTooltipDelayMs}
        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
      >
        <IconButton
          size="medium"
          aria-label={getActionAriaLabel('open', 'columns')}
          onClick={onColumnsOpen}
          onMouseEnter={() => {
            onSetHoverTarget('columns');
          }}
          onMouseLeave={() => {
            onSetHoverTarget(null);
          }}
          sx={[
            toolbarIconButtonSx,
            { color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor },
          ]}
        >
          <Box sx={toolbarIconCircleSx(hoverTarget === 'columns')}>
            <ViewColumnOutlinedIcon
              sx={{
                color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor,
                width: '22px !important',
                height: '22px !important',
              }}
            />
          </Box>
        </IconButton>
      </Tooltip>
      {quickSortOptions.map((option) => {
        const hoverKey = `quickSort:${option.key}` as const;
        const isActiveQuickSort = sortKey === option.key;
        const quickSortIconColor = isActiveQuickSort ? 'primary.main' : toolbarInactiveIconColor;
        const firstDirection = option.firstDirection ?? 'desc';
        const nextDirectionLabel = !isActiveQuickSort
          ? firstDirection === 'asc'
            ? 'ascending'
            : 'descending'
          : sortDirection === firstDirection
            ? firstDirection === 'asc'
              ? 'descending'
              : 'ascending'
            : 'reset';
        const quickSortCopy = getQuickSortActionCopy(option.label, nextDirectionLabel);
        return (
          <Tooltip
            key={option.key}
            title={quickSortCopy.tooltip}
            arrow
            enterDelay={toolbarIconTooltipDelayMs}
            slotProps={CLONE_TOOLTIP_SLOT_PROPS}
          >
            <IconButton
              size="medium"
              aria-label={quickSortCopy.ariaLabel}
              onClick={() => {
                onQuickSort(option.key, firstDirection);
              }}
              onMouseEnter={() => {
                onSetHoverTarget(hoverKey);
              }}
              onMouseLeave={() => {
                onSetHoverTarget(null);
              }}
              sx={[toolbarIconButtonSx, { color: quickSortIconColor }]}
            >
              <Box sx={toolbarIconCircleSx(hoverTarget === hoverKey)}>
                {renderQuickSortIcon(option.metric, quickSortIconColor)}
                {isActiveQuickSort ? renderQuickSortDirectionBadge(sortDirection) : null}
              </Box>
            </IconButton>
          </Tooltip>
        );
      })}
      <Tooltip
        title={getActionLabel('sort')}
        arrow
        enterDelay={toolbarIconTooltipDelayMs}
        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
      >
        <IconButton
          size="medium"
          aria-label={getActionAriaLabel('open', 'sort')}
          onClick={onSortOpen}
          onMouseEnter={() => {
            onSetHoverTarget('sort');
          }}
          onMouseLeave={() => {
            onSetHoverTarget(null);
          }}
          sx={[
            toolbarIconButtonSx,
            { color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor },
          ]}
        >
          <Box sx={toolbarIconCircleSx(hoverTarget === 'sort')}>
            <SortOutlinedIcon
              sx={{
                color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor,
                width: '22px !important',
                height: '22px !important',
              }}
            />
          </Box>
        </IconButton>
      </Tooltip>
      <Tooltip
        title={getActionLabel('reset')}
        arrow
        enterDelay={toolbarIconTooltipDelayMs}
        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
      >
        <Box
          component="span"
          sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
        >
          <IconButton
            size="medium"
            aria-label={getActionLabel('reset')}
            onClick={onReset}
            disabled={!hasActiveTableToolbarState}
            sx={[toolbarIconButtonSx, hasActiveTableToolbarState ? { color: 'primary.main' } : {}]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'reset' && hasActiveTableToolbarState)}>
              <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
            </Box>
          </IconButton>
        </Box>
      </Tooltip>
    </>
  );
};
